// src\app\modules\commonModule.jsx

import { all, call, put, take } from 'redux-saga/effects'
import * as api from '../services/api'
import { BASE, COLUMN, FORMATTING, JARGON, KEY_VALUES, KIND, LSE_INDEX_ENUM, RADIO, SCOPE, TAB, TABLE_BIT, VIEW } from "../modules/constants"
import { CHECKBOX } from '../modules/checkbox_constants'
import { defaultRollupStatus } from './appModule'
import history from '../services/history'
import { getTAB, getVisibleTabs } from './tabDefinitions'
import HOOK from '../components/hooks'

const { asc } = FORMATTING

const buildSearch = props => {
  // console.log('buildSearch props:', props)

  const { id, searchStringObj, tab } = props
  // NB: Don't confuse local searchStringObj with global const
  const searchString = searchStringObj[tab.INDEX]
  const pathId = id ? `/${id}` : ''

  const url = {
    pathname: `/${tab.PATH}${pathId}`,
    search: '',
  }

  // Appends valid parameters to url.search
  urlSearchParameters({ ...props, searchString, url })

  return `${url.pathname}${url.search}`
}

// Builds the url for each tab
const buildCurrentUrl = props => {
  // console.log('buildCurrentUrl props:', props)
  const { companyId, newsId, personId } = props

  return {
    [TAB[BASE.DASHBOARD].INDEX]: buildSearch({ ...props, tab: TAB[BASE.DASHBOARD] }),
    [TAB[BASE.COMPANY].INDEX]: companyId ? buildSearch({ ...props, tab: TAB[BASE.COMPANY], id: companyId }) : null,
    [TAB[BASE.FAVOURITES].INDEX]: buildSearch({ ...props, tab: TAB[BASE.FAVOURITES], id: null }),
    [TAB[BASE.LSE.ROLES].INDEX]: buildSearch({ ...props, tab: TAB[BASE.LSE.ROLES], id: null }),
    [TAB[BASE.NEWS_RELEASE].INDEX]: newsId ? buildSearch({ ...props, tab: TAB[BASE.NEWS_RELEASE], id: newsId }) : null,
    [TAB[BASE.LSE.COMPANIES].INDEX]: buildSearch({ ...props, tab: TAB[BASE.LSE.COMPANIES], id: null }),
    [TAB[BASE.LSE.NEWS].INDEX]: buildSearch({ ...props, tab: TAB[BASE.LSE.NEWS], id: null }),
    [TAB[BASE.LSE.ADVISERS].INDEX]: buildSearch({ ...props, tab: TAB[BASE.LSE.ADVISERS], id: null }),
    [TAB[BASE.LSE.PEOPLE].INDEX]: buildSearch({ ...props, tab: TAB[BASE.LSE.PEOPLE], id: null }),
    [TAB[BASE.PERSON].INDEX]: personId ? buildSearch({ ...props, tab: TAB[BASE.PERSON], id: personId }) : null,
    [TAB[BASE.SEARCH].INDEX]: buildSearch({ ...props, tab: TAB[BASE.SEARCH], id: null }),
    [TAB.RANDOM.INDEX]: buildSearch({ ...props, tab: TAB[BASE.SEARCH], scope: SCOPE.RANDOM, id: null }),
  }
}

const urlSearchParameters = props => {
  // Appends valid parameters to url.search
  // console.log('urlSearchParameters props:', props)
  const { checkboxes, nexusView, scope, searchString, seed, radio, the350club, url, urlSource, urlTrialCode } = props

  appendParameter({ url, tag: 'view', value: `${nexusView}` })
  appendParameter({ url, tag: 'radio', value: getRadioValue(radio) })
  appendParameter({ url, tag: 'scope', value: scope })
  appendParameter({ url, tag: 'search-string', value: searchString })
  appendParameter({ url, tag: 'seed', value: seed })
  appendParameter({ url, tag: 'trial', value: urlTrialCode })

  // Only write source to url if it is not "numerable"
  if (urlSource === JARGON.THE350CLUB || the350club === true)
    appendParameter({ url, tag: 'source', value: JARGON.THE350CLUB })

  appendCheckboxesToUrl({ checkboxes, url })
}

export const appendCheckboxesToUrl = ({ checkboxes, url }) => {
  checkboxes ??= CHECKBOX.DEFAULTS // Nullish coalescing assignment
  for (const [hook, value] of Object.entries(checkboxes))
    appendParameter({ url, tag: hook, value })
}

// Returns a string of the combined values on the objects properties
// Note that string are appended from left so the order is the reverse of the object props
// eg object {a: 1, b: 2, c: 3 } return 321
const getRadioValue = object => {
  var result = ""

  for (const [_, value] of Object.entries(object)) {
    result = value + result
  }
  return result
}

const appendParameter = ({ url, tag, value }) => {
  // If this is the 1st parameter, prefix '/?'
  // For subsequent parameter, prefix '&'
  const prefix = url.search ? '&' : '/?'

  // Only append the parameter if the value is 'true' or 0
  if (!!value || value === 0)
    url.search += `${prefix}${tag}=${encodeURIComponent(value)}`
}

// Set default values for checkboxes and nexusView
const checkboxes = CHECKBOX.DEFAULTS
const nexusView = VIEW.TABLE

// Init 'radio' from RADIO
const radio = {}
Object.keys(RADIO).forEach(function (key) {
  radio[key] = RADIO[key].DEFAULT
})

const searchStringObj = { 0: '', 1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: '', 8: '', 9: '', 10: '' }
// scopeObj will be required if we add scope to other searches (eg Roles)
// const scopeObj = { 0: 'companies', 1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: '' }
const tableInfoArray = () => {
  const array = []
  for (let index = 0; index < TAB.COUNT; index++)
    array[index] = { page: 0, rowsPerPage: KEY_VALUES.DEFAULT_ROWS_PER_PAGE }

  array[TAB[BASE.COMPANY].INDEX] = { page: 0, rowsPerPage: KEY_VALUES.DEFAULT_ROWS_PER_PAGE }
  array[TAB[BASE.PERSON].INDEX] = { page: 0, rowsPerPage: KEY_VALUES.DEFAULT_ROWS_PER_PAGE }
  array[TAB.RANDOM.INDEX] = { page: 0, rowsPerPage: KEY_VALUES.DEFAULT_ROWS_PER_PAGE }

  return array
}

const initTableOrder = () => {
  // NB We have two items in the array (eg [{ orderBy: COLUMN.OFFICER }, { orderBy: COLUMN.OFFICER }],)
  // because, 0 is for landscape and portrait 1
  const tableOrder = {
    // No sorting for companies house search
    [KIND.DASHBOARD]: [],
    [KIND.SEARCH.COMPANIES]: [{ orderBy: COLUMN.ORDER }, { orderBy: COLUMN.ORDER }],
    [KIND.SEARCH.OFFICERS]: [{ orderBy: COLUMN.ORDER }, { orderBy: COLUMN.ORDER }],

    // NB: prop 'order' is asc if omitted. eg [{ orderBy: COLUMN.OFFICER, order: asc }]
    [KIND.FAVOURITE.PEOPLE]: [{ orderBy: COLUMN.OFFICER }, { orderBy: COLUMN.OFFICER }],
    [KIND.FAVOURITE.COMPANIES]: [{ orderBy: COLUMN.COMPANY }, { orderBy: COLUMN.COMPANY }],

    // NB: lseCompaniesMeta comes sorted by company then index, so we set 'dontSort: true'
    // for these so that they are not sorted until 'common.setTableOrder' is called
    [KIND.LSE.COMPANIES.OVERVIEW]: [{ orderBy: COLUMN.LSE_INDEX, dontSort: true }, { orderBy: COLUMN.COMPANY }],
    [KIND.LSE.COMPANIES.BOARD]: [{ orderBy: -1, dontSort: true }, { orderBy: COLUMN.COMPANY }],
    [KIND.LSE.COMPANIES.GOVERNANCE]: [{ orderBy: -1, dontSort: true }, { orderBy: COLUMN.COMPANY }],
    [KIND.LSE.COMPANIES.COMMITTEES]: [{ orderBy: -1, dontSort: true }, { orderBy: COLUMN.COMPANY }],
    [KIND.LSE.COMPANIES.DATES]: [{ orderBy: -1, dontSort: true }, { orderBy: COLUMN.COMPANY }],
    [KIND.LSE.COMPANIES.ADVISERS]: [{ orderBy: -1, dontSort: true }, { orderBy: COLUMN.ADVISER }],

    [KIND.LSE.ROLES.BOARD]: [{ orderBy: COLUMN.COMPANY }, { orderBy: COLUMN.COMPANY }],
    [KIND.LSE.ROLES.COMMITTEES]: [{ orderBy: COLUMN.COMPANY }, { orderBy: COLUMN.COMPANY }],

    [KIND.LSE.PEOPLE.BOARD]: [{ orderBy: COLUMN.OFFICER }, { orderBy: COLUMN.OFFICER }],
    [KIND.LSE.PEOPLE.COMMITTEES]: [{ orderBy: COLUMN.OFFICER }, { orderBy: COLUMN.OFFICER }],

    [KIND.LSE.ADVISERS]: [{ orderBy: COLUMN.ADVISER }, { orderBy: COLUMN.ADVISER }],
    [KIND.LSE.NEWS]: [{ order: "desc", orderBy: COLUMN.PUBLICATION_DATE }, { order: "desc", orderBy: COLUMN.PUBLICATION_DATE }],

    [KIND.NEXUS.COMPANY.DASHBOARD]: initCompanyTableOrders(),
    [KIND.NEXUS.COMPANY.BOARD]: initCompanyTableOrders(),
    [KIND.NEXUS.COMPANY.NEWS]: [{ order: "desc", orderBy: COLUMN.PUBLICATION_DATE }, { order: "desc", orderBy: COLUMN.PUBLICATION_DATE }],
    [KIND.NEXUS.COMPANY.COMMITTEES]: [{ orderBy: COLUMN.OFFICER }, { orderBy: COLUMN.OFFICER }],
    [KIND.NEXUS.COMPANY.ADVISER_CLIENTS]: [{ orderBy: COLUMN.ADVISER_CLIENT }, { orderBy: COLUMN.ADVISER_CLIENT }],
    [KIND.NEXUS.COMPANY.CONNECTIONS]: initCompanyTableOrders(),

    [KIND.NEXUS.PERSON.ROLES]: initPersonTableOrders(),
    [KIND.NEXUS.PERSON.NEWS]: [{ order: "desc", orderBy: COLUMN.PUBLICATION_DATE }, { order: "desc", orderBy: COLUMN.PUBLICATION_DATE }],
    [KIND.NEXUS.PERSON.COMMITTEES]: [{ orderBy: COLUMN.COMPANY }, { orderBy: COLUMN.COMPANY }],
    [KIND.NEXUS.PERSON.CONNECTIONS]: initPersonTableOrders(),

    [KIND.NEWS_RELEASE]: [], // Not used directly, but required when following company link from new_release
  }

  return tableOrder
}

const initCompanyTableOrders = () => {
  const { CONNECTIONS, RESIGNED } = TABLE_BIT
  // Intialise the array so that we don't have to fill in all values.
  const array = setArrayDefaults({ orderBy: COLUMN.OFFICER, order: asc })
  array[CONNECTIONS] = { orderBy: COLUMN.LSE_INDEX }
  array[CONNECTIONS | RESIGNED] = { orderBy: COLUMN.LSE_INDEX }
  // Example:  array[CONNECTIONS | PORTRAIT | RESIGNED] = { orderBy: COLUMN.NAME }
  return array
}

const initPersonTableOrders = () => {
  // Intialise the array so that we don't have to fill in all values.
  // (See above for example of how to set specific combinations)
  return setArrayDefaults({ orderBy: COLUMN.LSE_INDEX, order: asc })
}

const setArrayDefaults = props => {
  // 'Or' all the bits from the TABLE_BIT object to give us the max size of the
  // array eg TABLE_BIT.CONNECTIONS | TABLE_BIT.PORTRAIT | TABLE_BIT.RESIGNED
  // NB: Only nexus page uses all combinations. HomePage only changes with aspect-ratio ie 'portrait'
  var x = 0
  for (var name in TABLE_BIT)
    x = x | TABLE_BIT[name]

  // Return an array using default orderBy value of size x (+1 for 0)
  return Array.apply(null, Array(x + 1)).map(() => (props))
}

const currentUrl = buildCurrentUrl({ checkboxes, nexusView, radio, searchStringObj })
// console.log("currentUrl", currentUrl)
// const TAB1 = getTAB(features)
// const currentUrls = buildCurrentUrls({ checkboxes, nexusView, radio, searchStringObj ,  TAB: TAB1 })
// console.log("currentUrls"  , currentUrls)

const initialState = {
  checkboxes,
  companyId: null,
  currentUrl,
  radio, // Governs tab radio button settings
  newsId: null,
  nexusView,
  personId: null,
  previousCheckboxes: checkboxes,
  searchStringObj,
  tabIndex: null, // No tab selected
  tableInfoArray: tableInfoArray(),
  tableOrder: initTableOrder(),
  urlTrialModalShown: false,
  the350club: false,
  urlSource: "numerable",
  urlTrial: false,
  urlTrialCode: null,
  loginUrl: "/",
  userCheckboxes: false, // Checkboxes from the user settings
}

export function reducer(state = initialState, action) {

  switch (action.type) {

    case 'common.saveSettings': {
      // console.log('common.saveSettings action:', action)
      const prevTabIndex = state.tabIndex
      return {
        ...state,
        checkboxes: action.checkboxes,
        // previousCheckboxes: action.previousCheckboxes,
        companyId: action.companyId,
        currentUrl: buildCurrentUrl(action),
        newsId: action.newsId,
        nexusView: action.nexusView,
        personId: action.personId,
        radio: action.radio,
        scope: action.scope,
        searchString: action.searchString,
        searchStringObj: action.searchStringObj,
        seed: action.seed,
        tab: action.tab,
        prevTabIndex,
        tabIndex: action.tab.INDEX,
      }
    }

    case 'common.saveSplash': {
      // console.log('common.saveSplash action:', action)
      const { urlSource, urlTrialCode } = action
      const the350club = action.urlSource === JARGON.THE350CLUB
      return {
        ...state,
        urlSource,
        the350club,
        urlTrial: action.daysLeft > 0,
        urlTrialCode,
        urlTrialDaysLeft: action.daysLeft,
        currentUrl: buildCurrentUrl(action),
      }
    }

    case 'common.setStandardTrialModalShown': {
      return {
        ...state,
        standardTrialModalShown: true,
      }
    }

    case 'common.setUrlTrialModalShown': {
      return {
        ...state,
        urlTrialModalShown: true,
      }
    }

    case 'common.saveTableInfo': {
      // console.log('common.saveTableInfo action', action)
      return {
        ...state,
        tableInfoArray: action.tableInfoArray,
      }
    }

    case 'common.setTableOrder': {
      // console.log('action:',action)
      // for (var name in COLUMN)
      //   if (COLUMN[name] === action.orderBy)
      //     console.log(`${action.orderBy}: ${name}`)

      const tableOrderKind = state.tableOrder[action.table]

      tableOrderKind[action.orderSet] = {
        orderBy: action.orderBy,
        order: action.order,
        dontSort: false,
      }

      return {
        ...state,
        tableOrder: {
          ...state.tableOrder,
          [action.table]: tableOrderKind,
        }
      }
    }

    case 'common.setCompanyTabUrl':
      return {
        ...state,
        currentUrl: {
          ...state.currentUrl,
          [TAB[BASE.COMPANY].INDEX]: action.url,
        },
      }

    case 'common.changeTab':
    case 'common.setTabIndex':
      // console.log('common.changeTab/setTabIndex action:',action)
      return {
        ...state,
        prevTabIndex: state.tabIndex,
        tabIndex: action.index,
      }

    case 'common.setLoginUrl':
      // console.log('common.setLoginUrl: action', action)
      return {
        ...state,
        loginUrl: action.pathname ?? "/",
      }

    case 'common.saveCheckboxes':
      // console.log('common.saveCheckboxes: action', action)
      return {
        ...state,
        userCheckboxes: true,
      }

    case 'common.setCheckboxes':
      // console.log('common.setCheckboxes: action', action)
      return {
        ...state,
        checkboxes: action.checkboxes,
        userCheckboxes: true,
      }

    // Dispatched by watchGetCheckboxes() once CHECKBOX.DEFAULTS reflects the logged-in user's own
    // saved checkboxes (merged on top of a freshly-reset, unpolluted baseline - see
    // resetCheckboxDefaults()) and any permission clamp for their current subscription level.
    // Applies it to the active session directly, rather than relying on the next page's
    // analyseUrl()/saveSettings() to happen to pick up the new defaults - that may not run before
    // the first post-login render (eg if the landing page doesn't unmount/remount on login).
    case 'common.applyCheckboxDefaults': {
      const checkboxes = { ...CHECKBOX.DEFAULTS }
      const newState = { ...state, checkboxes, previousCheckboxes: checkboxes }
      return { ...newState, currentUrl: buildCurrentUrl(newState) }
    }

    default: {
      return state
    }
  }
}
//#region  functions*
function* watchChangeTab() {
  while (true) {
    const props = yield take('common.changeTab')
    // console.log('common.changeTab props:', props)
    const { currentUrl, features, index, scope } = props
    const visibleTabs = getVisibleTabs(features)

    switch (index) {
      case TAB[BASE.LSE.ADVISERS].INDEX:
      case TAB[BASE.COMPANY].INDEX:
      case TAB[BASE.LSE.ROLES].INDEX:
      case TAB[BASE.LSE.PEOPLE].INDEX:
      case TAB[BASE.LSE.NEWS].INDEX:
      case TAB[BASE.LSE.COMPANIES].INDEX:
      case TAB[BASE.PERSON].INDEX:
      case TAB[BASE.NEWS_RELEASE].INDEX:
      case TAB[BASE.FAVOURITES].INDEX:
        if (currentUrl[index]) history.push(currentUrl[index])
        break

      case TAB[BASE.SEARCH].INDEX:
        history.push(scope === SCOPE.RANDOM
          ? currentUrl[TAB.RANDOM.INDEX]
          : currentUrl[TAB[BASE.SEARCH].INDEX])
        break

      case TAB[BASE.DASHBOARD].INDEX:
      default:
        history.push(currentUrl[TAB[BASE.DASHBOARD].INDEX])
        break
    }
  }
}

function* watchHistoryPush() {
  while (true) {
    const props = yield take('common.historyPush')
    // console.log('common.historyPush props:', props)
    const currentUrl = buildCurrentUrl(props)
    history.push(currentUrl[props.tabIndex])
  }
}

function* watchSelectRadio() {
  while (true) {
    const props = yield take('common.selectRadio')
    // console.log('common.selectRadio props:', props)
    const { eventTargetValue, radio, tabIndex } = props

    switch (tabIndex) {
      case TAB[BASE.LSE.COMPANIES].INDEX:
        radio[BASE.LSE.COMPANIES] = eventTargetValue
        break

      case TAB[BASE.LSE.ROLES].INDEX:
        radio[BASE.LSE.ROLES] = eventTargetValue
        break

      case TAB[BASE.COMPANY].INDEX:
        radio[BASE.COMPANY] = eventTargetValue
        break

      case TAB[BASE.PERSON].INDEX:
        radio[BASE.PERSON] = eventTargetValue
        break

      case TAB[BASE.LSE.PEOPLE].INDEX:
        radio[BASE.LSE.PEOPLE] = eventTargetValue
        break

      case TAB[BASE.LSE.NEWS].INDEX:
        radio[BASE.LSE.NEWS] = eventTargetValue
        break

      case TAB[BASE.FAVOURITES].INDEX:
        radio[BASE.FAVOURITES] = eventTargetValue
        break

      default:
        break
    }

    const currentUrl = buildCurrentUrl(props)
    history.push(currentUrl[tabIndex])
  }
}

function* watchSelectRow() {
  while (true) {
    const props = yield take('common.selectRow')
    // console.log('common.selectRow props:', props)

    const { searchStringObj, tabIndex } = props

    // Use props.pathname if supplied or location.pathname.
    // NB: Use trimSlash because location.pathname has '/' suffix whereas
    // props.pathname may not. We add the correct slashes in the push below.

    const url = {
      pathname: trimSlash(props.pathname ? props.pathname : props.location.pathname),
      search: ''
    }

    // Appends valid parameters to url.search
    const searchString = searchStringObj && tabIndex ? searchStringObj[tabIndex] : null

    urlSearchParameters({ ...props, searchString, url })

    history.push(`/${url.pathname}${url.search}`)
  }
}

function* watchSaveCheckboxes() {
  while (true) {
    const props = yield take('common.saveCheckboxes')
    // console.log('common.saveCheckboxes props:', props.checkboxes)
    yield call(api.checkboxes.saveCheckboxes, props.checkboxes)
  }
}

function* watchGetCheckboxes() {
  while (true) {
    const props = yield take('common.getCheckboxes')
    // console.log('common.getCheckboxes props:', props)

    const json = yield call(api.checkboxes.getCheckboxes)

    const savedCheckboxes = JSON.parse(json)
    if (savedCheckboxes) {
      // The Object.assign() method copies all properties from one or more source objects to a target object. It returns the modified target object.
      // ie changes the values in CHECKBOX.DEFAULTS to those from the user's saved checkboxes
      Object.assign(CHECKBOX.DEFAULTS, savedCheckboxes)
    }

    // Trial users are locked to FTSE 100 (and Favourites, which is a separate hook) within the
    // LSE Group selector, regardless of any broader selection saved from a previous (or
    // since-changed) subscription level
    if (props.features?.Trial)
      CHECKBOX.DEFAULTS[HOOK.LSE_INDEX] = CHECKBOX[HOOK.LSE_INDEX][LSE_INDEX_ENUM.FTSE_100].BIT

    // Apply the (now merged + permission-clamped) defaults to the active session. This saga only
    // ever runs when there's no url query string (see loadFavouritesAndLSEs.js), so there's no
    // url-specified checkbox state it could clobber.
    yield put({ type: 'common.applyCheckboxDefaults' })

    if (savedCheckboxes || props.features?.Trial) {
      // Recompute rollup open/closed state now that CHECKBOX.DEFAULTS reflects this user's saved
      // selections, not just the hardcoded defaults used when the rollups were first initialised
      yield put({ type: 'app.setRollupsStatus', statusByGroup: defaultRollupStatus() })
    }

    if (savedCheckboxes) {
      const currentUrl = buildCurrentUrl({ ...props, checkboxes: CHECKBOX.DEFAULTS })

      // Only go to dashboard if we have arrived via 'root'
      // (We may have, for example, come via news release ../news-release/...)
      if (location.pathname === '/')
        history.push(currentUrl[TAB[BASE.DASHBOARD].INDEX])
    }
  }
}
//#endregion

// Trim forward slashes from start and/or end of string
const trimSlash = str => str.replace(/^\/*(.*?)\/*$/, '$1')

export const actions = {
  changeTab: props => ({ type: 'common.changeTab', ...props }),
  historyPush: props => ({ ...props, type: 'common.historyPush' }),
  saveSettings: props => ({ ...props, type: 'common.saveSettings' }),
  saveSplash: props => ({ ...props, type: 'common.saveSplash' }),
  saveTableInfo: props => ({ ...props, type: 'common.saveTableInfo' }),
  selectRadio: props => ({ type: 'common.selectRadio', ...props }),
  selectRow: props => ({ type: 'common.selectRow', ...props }),
  setLoginUrl: props => ({ type: 'common.setLoginUrl', ...props }),
  setCompanyTabUrl: url => ({ type: 'common.setCompanyTabUrl', url }),
  setTabIndex: index => ({ type: 'common.setTabIndex', index }),
  setTableOrder: props => ({ type: 'common.setTableOrder', ...props }),
  setStandardTrialModalShown: props => ({ ...props, type: 'common.setStandardTrialModalShown' }),
  setUrlTrialModalShown: props => ({ ...props, type: 'common.setUrlTrialModalShown' }),
  getCheckboxes: props => ({ ...props, type: 'common.getCheckboxes' }),
  saveCheckboxes: checkboxes => ({ checkboxes, type: 'common.saveCheckboxes' }),
  setCheckboxes: checkboxes => ({ checkboxes, type: 'common.setCheckboxes' }),
}

export const selectors = {}

export function* sagas() {
  yield all([
    watchChangeTab(),
    watchHistoryPush(),
    watchSelectRadio(),
    watchSelectRow(),
    watchSaveCheckboxes(),
    watchGetCheckboxes(),
  ])
}
